import {
  fighterOf,
  type BattleEvent,
  type BattleState,
  type DamageType,
  type Vec3,
} from '@archon3d/core';
import type { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import type { Scene } from '@babylonjs/core/scene';
import { softDot } from '../softDot';

/** A look for a burst: the colours a spark is born and dies in, and how it flies. */
interface SparkLook {
  colors: [Color4, Color4];
  /** Metres, at true size: sparks the size of a fist, a blast a head's. */
  size: [number, number];
  life: [number, number];
  /** Metres a second. */
  speed: [number, number];
  gravity: number;
  /** How far up a spark heads, from -1 (straight down) to 1 (straight up). */
  rise?: [number, number];
  /** Light added to what is behind (sparks, fire), or colour laid over it (dust). */
  blend?: 'add' | 'standard';
}

const rgba = (r: number, g: number, b: number, a = 1) => new Color4(r, g, b, a);

/** A blow's sparks take the colour of what it does: steel white, fire orange, frost blue… */
const BY_DAMAGE: Record<DamageType, [Color4, Color4]> = {
  physical: [rgba(1, 0.86, 0.6), rgba(0.95, 0.45, 0.15)],
  piercing: [rgba(0.95, 0.95, 0.88), rgba(0.65, 0.62, 0.55)],
  fire: [rgba(1, 0.62, 0.15), rgba(0.9, 0.2, 0.05)],
  frost: [rgba(0.78, 0.92, 1), rgba(0.4, 0.62, 1)],
  arcane: [rgba(0.88, 0.62, 1), rgba(0.52, 0.3, 1)],
  venom: [rgba(0.65, 1, 0.3), rgba(0.3, 0.72, 0.15)],
  lightning: [rgba(0.96, 0.96, 1), rgba(0.62, 0.72, 1)],
};

const HIT: Omit<SparkLook, 'colors'> = {
  size: [0.05, 0.14],
  life: [0.18, 0.42],
  speed: [2, 5.5],
  gravity: -7,
};
/** A shot that meets a wall throws up dust and chips. */
const WALL: SparkLook = {
  colors: [rgba(0.8, 0.72, 0.58), rgba(0.45, 0.4, 0.32)],
  size: [0.06, 0.16],
  life: [0.25, 0.55],
  speed: [1, 3],
  gravity: -5,
};
/** A fighter that blows itself up (D-18): a ball of fire. */
const BLAST: SparkLook = {
  colors: [rgba(1, 0.92, 0.5), rgba(1, 0.35, 0.05)],
  size: [0.15, 0.45],
  life: [0.3, 0.8],
  speed: [3, 9],
  gravity: -2,
};

/** A body that falls throws up the dust it lands in (x-aaa-gap#8). */
const DUST: SparkLook = {
  colors: [rgba(0.62, 0.55, 0.45, 0.7), rgba(0.45, 0.4, 0.33, 0.45)],
  size: [0.25, 0.55],
  life: [0.6, 1.2],
  speed: [0.4, 1.4],
  gravity: -0.8,
  rise: [0, 0.45],
  blend: 'standard',
};
/**
 * The fallen's light leaves it: motes of its side's colour, lightened so they show added to a
 * dark arena, drift up from the body as it fades (x-aaa-gap#8).
 */
const moteLook = (hex: string): SparkLook => {
  const color = Color4.FromHexString(`${hex}ff`);
  const lit = (k: number, alpha: number) =>
    rgba(
      color.r + (1 - color.r) * k,
      color.g + (1 - color.g) * k,
      color.b + (1 - color.b) * k,
      alpha,
    );
  return {
    colors: [lit(0.55, 1), lit(0.25, 0.8)],
    size: [0.05, 0.13],
    life: [0.9, 1.7],
    speed: [0.15, 0.6],
    gravity: 1.1,
    rise: [0.55, 1],
  };
};

const hitLook = (type: DamageType): SparkLook => ({
  ...HIT,
  colors: BY_DAMAGE[type] ?? BY_DAMAGE.physical,
});

/** The most sparks one system holds; a burst beyond it is only fewer sparks. */
const CAPACITY = 160;
/** One update unit per frame at 60 Hz, so lifetimes are seconds and speeds metres a second. */
const FULL_PACE = 1 / 60;

const rand = (min: number, max: number) => min + Math.random() * (max - min);

/**
 * The duel's sparks (x-aaa-gap#5, client-arena:quality#9): a burst where a blow or a shot lands,
 * dust where a shot meets a wall, fire where a fighter blows itself up. One additive particle
 * system per look, made the first time it is needed and drawn only while its sparks live: a
 * draw call or two during a fight, none at rest, the same in the headset as on a screen. The
 * systems emit in the arena's own space, so on the table in the headset they stand in the model.
 */
export class DuelSparks {
  private readonly systems = new Map<string, { system: ParticleSystem; queue: Vec3[] }>();
  private readonly emitter: Mesh;
  private readonly sprite: Texture;
  private speed = 1;

  constructor(
    private readonly scene: Scene,
    private readonly arena: TransformNode,
  ) {
    // An empty mesh at the arena's origin: the systems emit in its space, which is the arena's.
    this.emitter = new Mesh('duelSparks', scene);
    this.emitter.parent = arena;
    this.emitter.isPickable = false;
    this.sprite = softDot(scene, 'duelSparks');
  }

  /**
   * Makes the systems a duel can use before its first blow: Babylon loads the particle shaders
   * and builds their effect in the background, and a system made at the first hit would drop
   * that burst's first frames. Idle systems cost nothing to draw.
   */
  prepare(state: BattleState, colors: readonly string[] = []): void {
    for (const id of state.seats) {
      const type = fighterOf(state, id).damageType;
      if (!this.systems.has(`hit-${type}`)) this.make(`hit-${type}`, hitLook(type));
    }
    if (!this.systems.has('wall')) this.make('wall', WALL);
    if (!this.systems.has('blast')) this.make('blast', BLAST);
    if (!this.systems.has('dust')) this.make('dust', DUST);
    for (const hex of colors)
      if (!this.systems.has(`motes-${hex}`)) this.make(`motes-${hex}`, moteLook(hex));
  }

  /** Dust along a body that has just fallen, from its feet to its head. */
  dust(from: Vec3, to: Vec3): void {
    this.burst('dust', DUST, from, 26, to);
  }

  /** Motes of a side's colour (`#rrggbb`) rising from a body as it fades, feet to head. */
  motes(from: Vec3, to: Vec3, hex: string): void {
    this.burst(`motes-${hex}`, moteLook(hex), from, 36, to);
  }

  /** Sparks for what the simulation just did; `state` is the duel after it. */
  react(event: BattleEvent, state: BattleState): void {
    switch (event.type) {
      case 'hit': {
        // The ground's burn every step and a burst's cost to its own owner are no blows.
        if (event.source === 'hazard' || event.source === 'cost') return;
        const target = fighterOf(state, event.target);
        const by = fighterOf(state, event.by);
        const severity = Math.min(1, event.damage / Math.max(1, target.maxHp));
        const at = { x: target.pos.x, y: target.pos.y + target.height * 0.6, z: target.pos.z };
        this.burst(
          `hit-${by.damageType}`,
          hitLook(by.damageType),
          at,
          Math.round(10 + 22 * severity),
        );
        return;
      }
      case 'projectile-hit-world':
        this.burst('wall', WALL, event.at, 9);
        return;
      case 'suicide':
        this.burst('blast', BLAST, { ...event.at, y: event.at.y + 0.8 }, 70);
        return;
    }
  }

  /** How fast the sparks fly and fade: 1, or less while a kill plays in slow motion. */
  set pace(value: number) {
    if (value === this.speed) return;
    this.speed = value;
    for (const { system } of this.systems.values()) system.updateSpeed = FULL_PACE * value;
  }

  /** Every spark goes at once, as the duel's world is switched off. */
  clear(): void {
    for (const { system, queue } of this.systems.values()) {
      queue.length = 0;
      system.manualEmitCount = 0;
      system.reset();
    }
  }

  /** Every look that has been used so far, for tests: how many sparks each has alive. */
  alive(): Record<string, number> {
    const alive: Record<string, number> = {};
    for (const [key, { system }] of this.systems) alive[key] = system.getActiveCount();
    return alive;
  }

  dispose(): void {
    for (const { system } of this.systems.values()) system.dispose(false);
    this.systems.clear();
    this.sprite.dispose();
    this.emitter.dispose();
  }

  /** Sparks born at `at`, or spread along the line from it to `to`. */
  private burst(key: string, look: SparkLook, at: Vec3, count: number, to?: Vec3): void {
    const entry = this.systems.get(key) ?? this.make(key, look);
    // A spark that finds the system full is never born, and would leave its place in the queue
    // for the next burst's first spark: a full system takes only what it has room for.
    const room = CAPACITY - entry.system.getActiveCount() - entry.queue.length;
    for (let i = 0; i < Math.min(count, room); i++) {
      const k = to ? Math.random() : 0;
      entry.queue.push(
        to
          ? {
              x: at.x + (to.x - at.x) * k,
              y: at.y + (to.y - at.y) * k,
              z: at.z + (to.z - at.z) * k,
            }
          : at,
      );
    }
    // Sizes are drawn as they are, whatever the arena's scale: on the table in the headset the
    // arena is a model, and its sparks have to be made as small as it.
    const scale = Math.abs(this.arena.scaling.x) || 1;
    entry.system.minSize = look.size[0] * scale;
    entry.system.maxSize = look.size[1] * scale;
    entry.system.manualEmitCount = entry.queue.length;
  }

  private make(key: string, look: SparkLook) {
    const system = new ParticleSystem(`duelSparks-${key}`, CAPACITY, this.scene);
    const queue: Vec3[] = [];
    system.particleTexture = this.sprite;
    system.emitter = this.emitter;
    system.isLocal = true;
    system.updateSpeed = FULL_PACE * this.speed;
    system.blendMode =
      look.blend === 'standard' ? ParticleSystem.BLENDMODE_STANDARD : ParticleSystem.BLENDMODE_ADD;
    system.minLifeTime = look.life[0];
    system.maxLifeTime = look.life[1];
    system.minEmitPower = look.speed[0];
    system.maxEmitPower = look.speed[1];
    system.color1 = look.colors[0];
    system.color2 = look.colors[1];
    system.colorDead = new Color4(0, 0, 0, 0);
    system.gravity = new Vector3(0, look.gravity, 0);
    system.emitRate = 0;
    // Each spark is born at the burst it belongs to, in the order they came.
    system.startPositionFunction = (_matrix, out) => {
      const at = queue.shift();
      if (at) out.set(at.x, at.y, at.z);
    };
    const [low, high] = look.rise ?? [-0.4, 1];
    system.startDirectionFunction = (_matrix, out) => {
      // Out in every direction, a little more up than down unless the look says otherwise.
      const theta = rand(0, Math.PI * 2);
      const up = rand(low, high);
      const side = Math.sqrt(Math.max(0, 1 - up * up));
      out.set(Math.cos(theta) * side, up, Math.sin(theta) * side);
    };
    system.start();
    const entry = { system, queue };
    this.systems.set(key, entry);
    return entry;
  }
}
